import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Check, CheckCircle2, ChevronDown, ChevronRight, Clock, PhoneForwarded, Ticket, X, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ForwardingCompatibilitySheet } from '../components/billing/ForwardingCompatibilitySheet';
import { SUBSCRIPTION_PLANS, SubscriptionPlanId, formatCurrency, getSubscriptionPlan, PromoCodeResult, validatePromoCode } from '../data/subscriptionPlans';
import { useTheme } from '../contexts/ThemeContext';
interface PlanSelectionScreenProps {
  onSelectPlan: (planId: SubscriptionPlanId) => void;
  onBack?: () => void;
  /** Plan to preselect (e.g. when returning from consent). */
  selectedPlanId?: SubscriptionPlanId;
  promoCode?: string;
  onPromoCodeChange?: (value: string) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function PlanSelectionScreen({
  onSelectPlan,
  onBack,
  selectedPlanId,
  promoCode = '',
  onPromoCodeChange
}: PlanSelectionScreenProps) {
  const {
    isLight
  } = useTheme();
  // Only one package is offered; ignore any stale selection that no longer exists.
  const selected: SubscriptionPlanId = SUBSCRIPTION_PLANS.find(p => p.id === selectedPlanId)?.id ?? SUBSCRIPTION_PLANS[0].id;
  const [compatOpen, setCompatOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoDraft, setPromoDraft] = useState(promoCode);
  const [promoResult, setPromoResult] = useState<PromoCodeResult | null>(() => validatePromoCode(promoCode));
  const [promoError, setPromoError] = useState('');
  useEffect(() => {
    setPromoDraft(promoCode);
    setPromoResult(validatePromoCode(promoCode));
  }, [promoCode]);
  const plan = getSubscriptionPlan(selected);
  const trialEnd = new Date();
  trialEnd.setDate(trialEnd.getDate() + 3);
  const trialEndLabel = trialEnd.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
  const applyPromoCode = () => {
    const result = validatePromoCode(promoDraft);
    if (!result) {
      setPromoResult(null);
      setPromoError('This code is not valid. Check the code and try again.');
      return;
    }
    setPromoError('');
    setPromoResult(result);
    onPromoCodeChange?.(result.code);
    setPromoOpen(false);
  };
  const clearPromoCode = () => {
    setPromoDraft('');
    setPromoResult(null);
    setPromoError('');
    onPromoCodeChange?.('');
  };
  const muted = isLight ? 'text-slate-500' : 'text-zinc-400';
  const strong = isLight ? 'text-slate-900' : 'text-white';
  return <div className={`relative flex h-full flex-col ${isLight ? 'bg-slate-50' : 'bg-black'}`} data-id="element-2773" data-mp="222vog9z6h10o">
      <main className="flex-1 overflow-y-auto px-6 pb-6 pt-5" data-id="element-2774" data-mp="263xfw4mus8s3">
        <div className="mb-6 flex items-center justify-between" data-id="element-2775" data-mp="ah2mztijcez">
          {onBack ? <button type="button" onClick={onBack} aria-label="Back" className={`-ml-2 rounded-full p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-500 hover:bg-white' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-2776" data-mp="4bsvde7oodre">
              <ArrowLeft className="h-5 w-5" data-id="element-2777" data-mp="1ykeob9zjn77w" />
            </button> : <span className="w-9" data-id="element-2778" data-mp="1trvdgks8qq6s" />}
          <span className={`text-xs font-bold ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2779" data-mp="1tr3hh8uqeqfz">1 of 3</span>
        </div>

        <h1 className={`text-[28px] font-bold leading-tight tracking-tight ${strong}`} data-id="element-2780" data-mp="23izr9h66k501">Choose your plan</h1>
        <p className={`mt-2 text-sm leading-relaxed ${muted}`} data-id="element-2781" data-mp="2hj0tp123fkhm">
          Every plan includes your AI assistant, call summaries, scheduling and caller experience controls.
        </p>

        {/* Carrier notice — opens full details */}
        <motion.button type="button" onClick={() => setCompatOpen(true)} initial={reduceMotion ? false : {
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.25,
        ease: EASE,
        delay: 0.1
      }} whileTap={{
        scale: 0.985
      }} className={`group mt-5 flex w-full items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 ${isLight ? 'bg-slate-900 shadow-[0_10px_24px_-14px_rgba(15,23,42,0.6)] hover:bg-slate-800' : 'border border-white/10 bg-white/[0.06] hover:bg-white/[0.09]'}`} data-id="element-2782" data-mp="n4zv8haggzi2">
          <span className="relative grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-amber-400/15 text-amber-300 ring-1 ring-inset ring-amber-300/25" aria-hidden data-id="element-2783" data-mp="5vf5u34zr15r">
            <PhoneForwarded className="h-4 w-4" data-id="element-2784" data-mp="fn1qsr74selp" />
            <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5" data-id="element-2785" data-mp="3ebqplykejr48">
              {!reduceMotion && <motion.span className="absolute inset-0 rounded-full bg-amber-400" initial={{
              opacity: 0.7,
              scale: 1
            }} animate={{
              opacity: 0,
              scale: 2.2
            }} transition={{
              duration: 1.2,
              ease: 'easeOut',
              repeat: 2,
              repeatDelay: 0.8,
              delay: 0.5
            }} data-id="element-2786" data-mp="3j8qy0g4q55yi" />}
              <span className={`relative h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ${isLight ? 'ring-slate-900' : 'ring-[#1a1a1a]'}`} data-id="element-2787" data-mp="2wta6ds4defwt" />
            </span>
          </span>
          <span className="min-w-0 flex-1" data-id="element-2788" data-mp="2o0yh49kzsyif">
            <span className="block text-[13.5px] font-semibold tracking-tight text-white" data-id="element-2789" data-mp="2o06jkt7u4qpi">Check call forwarding first</span>
            <span className="mt-0.5 block text-[12px] leading-snug text-white/60" data-id="element-2790" data-mp="2j2540i9mb9is">
              Confirm your provider supports it before subscribing.
            </span>
          </span>
          <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-white/10 text-white transition-colors duration-150 group-hover:bg-white/15" aria-hidden data-id="element-2791" data-mp="293635lv3gi6c">
            <ChevronRight className="h-3.5 w-3.5 transition-transform duration-150 group-hover:translate-x-px" data-id="element-2792" data-mp="1uj3b7jnqgki6" />
          </span>
        </motion.button>

        {/* The single plan */}
        <section aria-label={`${plan.name} plan`} className={`mt-3 rounded-2xl border-2 px-4 py-3.5 ${isLight ? 'border-vexu bg-white' : 'border-vexu bg-vexu/[0.08]'}`} data-id="element-2793" data-mp="1lhve34wydppo">
          <div className="flex items-center justify-between gap-3" data-id="element-2794" data-mp="2e5hrt5zwaaq9">
            <div className="flex min-w-0 items-center gap-2" data-id="element-2795" data-mp="2i6ta0qgfvtof">
              <span className="grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-vexu text-white" aria-hidden data-id="element-2796" data-mp="24f6c6m6umd3j">
                <Check className="h-3 w-3" strokeWidth={3} data-id="element-2797" data-mp="29dpwyj9phiub" />
              </span>
              <h2 className={`text-[15px] font-bold ${strong}`} data-id="element-2798" data-mp="2x3tzenb5rcjq">{plan.name}</h2>
            </div>
            <p className="flex-shrink-0 whitespace-nowrap" data-id="element-2799" data-mp="31uk7w9evrvin">
              <span className={`text-xl font-extrabold tracking-tight ${strong}`} data-id="element-2800" data-mp="31w3ra8vu2mon">{formatCurrency(plan.price)}</span>
              <span className={`ml-1 text-xs font-medium ${muted}`} data-id="element-2801" data-mp="3b79huumwcebt">/ mo</span>
            </p>
          </div>
          <div className={`mt-3 flex items-center gap-4 border-t pt-3 text-xs ${isLight ? 'border-slate-100' : 'border-white/10'}`} data-id="element-2802" data-mp="375xzna6cqvdn">
            <span className={`flex items-center gap-1.5 whitespace-nowrap font-semibold ${strong}`} data-id="element-2803" data-mp="3g1dp7f02qodf">
              <Clock className="h-3.5 w-3.5 text-vexu" data-id="element-2804" data-mp="2sb9opgi6n4qn" />
              {plan.minutes.toLocaleString()} min / month
            </span>
            <span className={`flex items-center gap-1.5 whitespace-nowrap ${muted}`} data-id="element-2805" data-mp="2ob99vur68rj2">
              <Zap className="h-3.5 w-3.5 text-amber-500" data-id="element-2806" data-mp="329z4n3qiqf49" />
              {formatCurrency(plan.overageRate)}/min after
            </span>
          </div>
        </section>

        {/* Trial & overage terms */}
        <div className={`mt-4 space-y-1.5 px-1 text-[12px] leading-relaxed ${muted}`} data-id="element-2807" data-mp="hkji4kyc1ckf">
          <p data-id="element-2808" data-mp="cj41d536ts7u">
            <strong className={isLight ? 'text-vexu-700' : 'text-vexu'} data-id="element-2809" data-mp="clq50tmyjnks">3 days free.</strong> $0 today — you're charged{' '}
            {formatCurrency(plan.price)}/month from {trialEndLabel} unless you cancel before then.
          </p>
          <p data-id="element-2810" data-mp="2g00jo70n2zat">
            Minutes reset every month. If you go over, extra minutes are billed at {formatCurrency(plan.overageRate)}/min
            on your {plan.name} plan.
          </p>
        </div>

        {/* Promo code — collapsed by default */}
        <div className={`mt-5 border-t pt-4 ${isLight ? 'border-slate-200' : 'border-white/10'}`} data-id="element-2811" data-mp="2ps6b9fmzbeph">
          {promoResult ? <div className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs font-semibold ${isLight ? 'bg-vexu/[0.08] text-vexu-700' : 'bg-vexu/10 text-vexu'}`} data-id="element-2812" data-mp="2yr02srlgdew6">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" data-id="element-2813" data-mp="14cz3y6pwkrw3" />
              <span className="min-w-0 flex-1" data-id="element-2814" data-mp="14ejyhwc7waty">
                {promoResult.code} applied · {promoResult.description}
              </span>
              <button type="button" onClick={clearPromoCode} aria-label="Remove promo code" className="rounded-md p-1 transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu" data-id="element-2815" data-mp="11s17wy6whlu0">
                <X className="h-3.5 w-3.5" data-id="element-2816" data-mp="n3wtw7islyhk" />
              </button>
            </div> : <>
              <button type="button" onClick={() => setPromoOpen(o => !o)} aria-expanded={promoOpen} aria-controls="promo-panel" className={`flex items-center gap-2 text-sm font-semibold ${isLight ? 'text-slate-700 hover:text-slate-900' : 'text-zinc-300 hover:text-white'}`} data-id="element-2817" data-mp="1kq41lqae86y5">
                <Ticket className={`h-4 w-4 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2818" data-mp="3viyd973vp468" />
                Have a promo code?
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${promoOpen ? 'rotate-180' : ''}`} data-id="element-2819" data-mp="3rgjwcsjrsehl" />
              </button>
              <AnimatePresence initial={false} data-id="element-2820" data-mp="2yhh7l629ajgz">
                {promoOpen && <motion.div id="promo-panel" initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="overflow-hidden" data-id="element-2821" data-mp="2ujtdg58e0uq5">
                    <div className="flex gap-2 pt-3" data-id="element-2822" data-mp="3d7esnd4w2t1y">
                      <label htmlFor="promo-code" className="sr-only" data-id="element-2823" data-mp="3dasyivcrhke5">
                        Promo code
                      </label>
                      <input id="promo-code" autoFocus value={promoDraft} onChange={e => {
                  setPromoDraft(e.target.value.toUpperCase());
                  setPromoError('');
                }} onKeyDown={e => {
                  if (e.key === 'Enter') applyPromoCode();
                }} placeholder="Enter promo code" autoCapitalize="characters" aria-describedby="promo-code-feedback" aria-invalid={Boolean(promoError)} className={`min-w-0 flex-1 rounded-xl border px-3.5 py-3 text-sm font-semibold uppercase tracking-[0.08em] outline-none transition-colors placeholder:normal-case placeholder:font-normal placeholder:tracking-normal ${promoError ? isLight ? 'border-red-300 bg-red-50 text-slate-900' : 'border-red-500/40 bg-red-500/10 text-white' : isLight ? 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-vexu focus:ring-2 focus:ring-vexu/15' : 'border-white/10 bg-white/5 text-white placeholder:text-zinc-600 focus:border-vexu'}`} data-id="element-2824" data-mp="3m8wihn83m93u" />
                      <button type="button" onClick={applyPromoCode} disabled={!promoDraft.trim()} className="flex-shrink-0 rounded-xl bg-vexu px-4 text-xs font-bold text-white transition-colors hover:bg-vexu-600 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu focus-visible:ring-offset-2" data-id="element-2825" data-mp="2myq3d6ympmwd">
                        Apply
                      </button>
                    </div>
                    <p id="promo-code-feedback" aria-live="polite" className={`min-h-[20px] pt-1.5 text-[11px] font-medium ${isLight ? 'text-red-600' : 'text-red-400'}`} data-id="element-2826" data-mp="2wwnq1q0ubbmv">
                      {promoError}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </>}
        </div>
      </main>

      <ForwardingCompatibilitySheet open={compatOpen} onClose={() => setCompatOpen(false)} data-id="element-2827" data-mp="3ryejirw6nqi9" />

      <footer className={`border-t px-6 pb-7 pt-4 ${isLight ? 'border-slate-200 bg-white' : 'border-white/10 bg-black'}`} data-id="element-2828" data-mp="s4rogcz57g5">
        <Button fullWidth onClick={() => onSelectPlan(selected)} className="py-3.5" data-id="element-2829" data-mp="5olgzdvwtrut">
          Continue with {plan.name}
        </Button>
      </footer>
    </div>;
}